import { format, parse } from 'date-fns'

const DEFAULT_TIMEZONE = 'America/Vancouver'

export const toDateString = (timestamp, { showTime } = { showTime: false }) => {
  if (!timestamp) return ''
  return showTime ? format(timestamp, 'yyyy MMM d, p') : format(timestamp, 'yyyy MMM d')
}

export const toDateStringNumeric = (timestamp, { showTime } = { showTime: false }) => {
  if (!timestamp) return ''
  return showTime ? format(timestamp, 'yyyy-MM-dd, p') : format(timestamp, 'yyyy-MM-dd')
}

export const fromDateString = (dateString) => {
  if (!dateString) return null
  return parse('yyyy MMM d', dateString)
}

export const dateStringForReports = (timestamp) => {
  if (!timestamp) return ''
  return format(timestamp, 'yyyy MM dd')
}

export const dateStringForReportsTitle = (timestamp) => {
  if (!timestamp) return ''
  return format(timestamp, 'yyyy.MM.dd')
}

export const getTodaysDateFileName = () => new Date().toISOString().split('T')[0].replace(/-/g, '.')

/**
 * Format a date in a specific timezone
 * @param {Date|string|number} timestamp - The date to format
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/Vancouver')
 * @param {Object} options - Formatting options
 * @param {boolean} options.showTime - Whether to include time in the output
 * @returns {string} Formatted date string
 */
export const toDateStringInTimezone = (timestamp, timezone = DEFAULT_TIMEZONE, { showTime } = { showTime: false }) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
    ...(showTime && { hour: 'numeric', minute: '2-digit' }),
  }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

/**
 * Format a date in numeric format in a specific timezone
 * @param {Date|string|number} timestamp - The date to format
 * @param {string} timezone - IANA timezone identifier
 * @param {Object} options - Formatting options
 * @param {boolean} options.showTime - Whether to include time in the output
 * @returns {string} Formatted date string (yyyy-MM-dd format)
 */
export const toDateStringNumericInTimezone = (
  timestamp,
  timezone = DEFAULT_TIMEZONE,
  { showTime } = { showTime: false }
) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const dateOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }
  const parts = new Intl.DateTimeFormat('en-CA', dateOptions).formatToParts(date)
  const year = parts.find((p) => p.type === 'year')?.value
  const month = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value
  let result = `${year}-${month}-${day}`
  if (showTime) {
    const timeOptions = { hour: 'numeric', minute: '2-digit', timeZone: timezone }
    const timeStr = new Intl.DateTimeFormat('en-US', timeOptions).format(date)
    result += `, ${timeStr}`
  }
  return result
}

/**
 * Get the current date/time in a specific timezone
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date} Date object representing current time adjusted to timezone
 */
export const getNowInTimezone = (timezone = DEFAULT_TIMEZONE) =>
  new Date(new Date().toLocaleString('en-US', { timeZone: timezone }))

/**
 * Get the short timezone abbreviation (e.g., 'PST', 'EST', 'PDT')
 * @param {Date|string|number} timestamp - The date to get timezone for (needed for DST)
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Short timezone abbreviation
 */
export const getTimezoneAbbreviation = (timestamp, timezone = DEFAULT_TIMEZONE) => {
  const date = new Date(timestamp || Date.now())
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  }).formatToParts(date)
  return parts.find((p) => p.type === 'timeZoneName')?.value || ''
}

/**
 * Format a date in a specific timezone with timezone indicator (e.g., "Jan 5, 2026 PST")
 * @param {Date|string|number} timestamp - The date to format
 * @param {string} timezone - IANA timezone identifier
 * @param {Object} options - Formatting options
 * @param {boolean} options.showTime - Whether to include time in the output
 * @returns {string} Formatted date string with timezone indicator
 */
export const toDateStringWithTzIndicator = (
  timestamp,
  timezone = DEFAULT_TIMEZONE,
  { showTime } = { showTime: false }
) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
    ...(showTime && { hour: 'numeric', minute: '2-digit' }),
  }
  const formatted = new Intl.DateTimeFormat('en-US', options).format(date)
  const tzAbbrev = getTimezoneAbbreviation(date, timezone)
  return `${formatted} ${tzAbbrev}`
}
